import ArticleForm from '@src/pages/tenant/article/ArticleForm';
import ArticleList from '@src/pages/tenant/article/ArticleList';
import ArticlePreviewForm from '@src/pages/tenant/article/ArticlePreviewForm';
import TenantLogin from '@src/pages/tenant/auth/Login';
import TenantResetPassword from '@src/pages/tenant/auth/ResetPassword';
import SendResetPasswordEmail from '@src/pages/tenant/auth/SendResetPasswordEmail';
import TenantSetNewPassword from '@src/pages/tenant/auth/SetNewPassword';
import TenantLogout from '@src/pages/tenant/auth/TenantLogout';
import TenantTwoFactor from '@src/pages/tenant/auth/TenantTwoFactor';
import CommunityChannelActivityList from '@src/pages/tenant/community/CommunityChannelActivityList';
import CommunityIndex from '@src/pages/tenant/community/CommunityIndex';
import CompanyEdit from '@src/pages/tenant/company/CompanyEdit';
import CompanyList from '@src/pages/tenant/company/CompanyList';
import CompanyUserEdit from '@src/pages/tenant/company-user/CompanyUserEdit.tsx';
import CompanyUserList from '@src/pages/tenant/company-user/CompanyUserList';
import DxDiagnosisList from '@src/pages/tenant/dx-diagnosis/DxDiagnosisList';
import DxDiagnosisResults from '@src/pages/tenant/dx-diagnosis/DxDiagnosisResults';
import EventFormPage from '@src/pages/tenant/event/EventForm';
import EventList from '@src/pages/tenant/event/EventList';
import EventPreviewForm from '@src/pages/tenant/event/EventPreviewForm';
import ListParticipant from '@src/pages/tenant/list-participant/ListParticipant';
import MediaCategoryList from '@src/pages/tenant/media/MediaCategoryList';
import MediaTagList from '@src/pages/tenant/media/MediaTagList';
import Profile from '@src/pages/tenant/profile/Profile';
import SupportServiceCategoryList from '@src/pages/tenant/support-service/SupportServiceCategoryList';
import SupportServicePreviewForm from '@src/pages/tenant/support-service/SupportServicePreviewForm';
import SupportServicesFormPage from '@src/pages/tenant/support-service/SupportServicesForm';
import SupportServicesList from '@src/pages/tenant/support-service/SupportServicesList';
import SupportServiceTagList from '@src/pages/tenant/support-service/SupportServiceTagList';
import TenantUserCreate from '@src/pages/tenant/tenant-user/TenantUserCreate';
import TenantUserEdit from '@src/pages/tenant/tenant-user/TenantUserEdit';
import TenantUserList from '@src/pages/tenant/tenant-user/TenantUserList';
import TenantHome from '@src/pages/tenant/TenantHome';
import TenantsEdit from '@src/pages/tenant/tenants/TenantsEdit';
import { responsiveStylesType } from '@src/types/responsiveStylesType';

type TenantRouteType = {
  path: string;
  title: string;
  description?: string;
  element: React.ReactElement;
  responsive: responsiveStylesType;
};

const tenantPath = (suffix: string): string => {
  return `/tenant/${suffix}`;
};

export const TENANT_URL = {
  HOME: tenantPath(''),
  LOGIN: tenantPath('login'),
  TWO_FACTOR: tenantPath('two-factor'),
  FIRST_PASSWORD_RESET: tenantPath('password/edit'),
  PASSWORD_RESET_SENDMAIL: tenantPath('password/reset'),
  PASSWORD_RESET: tenantPath('password/reset/:token'),
  ARTICLES_CREATE: tenantPath('articles/create'),
  ARTICLES_EDIT: tenantPath('articles/:id/edit'),
  ARTICLES_PREVIEW: tenantPath('articles/:uuid'),
  ARTICLES: tenantPath('articles'),
  MEDIA_CATEGORIES: tenantPath('media-categories'),
  MEDIA_TAGS: tenantPath('media-tags'),
  DX_DIAGNOSIS: tenantPath('dx-diagnosis'),
  DX_DIAGNOSIS_RESULTS: tenantPath('dx-diagnosis/:id/results'),
  SUPPORT_SERVICES: tenantPath('support-services'),
  SUPPORT_SERVICES_CREATE: tenantPath('support-services/create'),
  SUPPORT_SERVICES_EDIT: tenantPath('support-services/:id/edit'),
  SUPPORT_SERVICES_PREVIEW: tenantPath('support-services/:uuid'),
  SUPPORT_SERVICES_CATEGORIES: tenantPath('support-services/categories'),
  SUPPORT_SERVICES_TAGS: tenantPath('support-services/tags'),
  COMPANIES: tenantPath('companies'),
  COMPANIES_EDIT: tenantPath('companies/:id/edit'),
  EVENTS: tenantPath('events'),
  EVENTS_CREATE: tenantPath('events/create'),
  EVENTS_PREVIEW: tenantPath('events/:uuid'),
  EVENTS_EDIT: tenantPath('events/:id/edit'),
  PROFILE: tenantPath('profile'),
  TENANTS_EDIT: tenantPath('tenants/edit'),
  TENANT_USERS: tenantPath('tenant-users'),
  TENANT_USERS_EDIT: tenantPath('tenant-users/:id/edit'),
  TENANT_USERS_CREATE: tenantPath('tenant-users/create'),
  COMPANY_USERS: tenantPath('company-users'),
  COMPANY_USERS_EDIT: tenantPath('company-users/:id/edit'),
  COMMUNITY_DASHBOARD: tenantPath('community/dashboard'),
  COMMUNITY_LIST_PARTICIPANT: tenantPath('community/list-participants'),
  COMMUNITY_CHANNEL_ACTIVITY: tenantPath('community/list-channels'),
  LOGOUT: tenantPath('logout'),
};

const tenantRoutes: TenantRouteType[] = [
  // ホーム
  {
    path: TENANT_URL.HOME,
    title: '記事管理',
    element: <TenantHome />,
    responsive: false,
  },
  // ログイン
  {
    path: TENANT_URL.LOGIN,
    title: 'ログイン',
    description: import.meta.env.VITE_GENERAL_META_DESCRIPTION,
    element: <TenantLogin />,
    responsive: ['desktop', 'tablet', 'mobile'],
  },
  // 認証コード入力
  {
    path: TENANT_URL.TWO_FACTOR,
    title: '認証コード入力',
    element: <TenantTwoFactor />,
    responsive: ['desktop', 'tablet', 'mobile'],
  },
  // 初回パスワード変更
  {
    path: TENANT_URL.FIRST_PASSWORD_RESET,
    title: 'パスワード設定',
    element: <TenantSetNewPassword />,
    responsive: ['desktop', 'tablet', 'mobile'],
  },
  // パスワード再発行（メール送信）
  {
    path: TENANT_URL.PASSWORD_RESET_SENDMAIL,
    title: 'パスワード設定',
    element: <SendResetPasswordEmail />,
    responsive: ['desktop', 'tablet', 'mobile'],
  },
  // パスワード再発行（再設定）
  {
    path: TENANT_URL.PASSWORD_RESET,
    title: 'パスワード設定',
    element: <TenantResetPassword />,
    responsive: ['desktop', 'tablet', 'mobile'],
  },
  // 記事一覧
  {
    path: TENANT_URL.ARTICLES,
    title: '記事管理',
    element: <ArticleList />,
    responsive: ['desktop'],
  },
  // 記事作成
  {
    path: TENANT_URL.ARTICLES_CREATE,
    title: '記事管理',
    element: <ArticleForm />,
    responsive: ['desktop'],
  },
  // 記事編集
  {
    path: TENANT_URL.ARTICLES_EDIT,
    title: '記事管理',
    element: <ArticleForm />,
    responsive: ['desktop'],
  },
  // 記事プレビュー
  {
    path: TENANT_URL.ARTICLES_PREVIEW,
    title: '記事管理',
    element: <ArticlePreviewForm />,
    responsive: ['desktop'],
  },
  // メディアカテゴリー一覧
  {
    path: TENANT_URL.MEDIA_CATEGORIES,
    title: 'メディア-カテゴリ管理',
    element: <MediaCategoryList />,
    responsive: ['desktop'],
  },
  // メディアタグ一覧
  {
    path: TENANT_URL.MEDIA_TAGS,
    title: 'メディア-タグ管理',
    element: <MediaTagList />,
    responsive: ['desktop'],
  },
  // DX診断結果一覧
  {
    path: TENANT_URL.DX_DIAGNOSIS,
    title: 'DX診断一覧',
    element: <DxDiagnosisList />,
    responsive: ['desktop'],
  },
  // DX診断結果閲覧
  {
    path: TENANT_URL.DX_DIAGNOSIS_RESULTS,
    title: 'DX診断結果',
    element: <DxDiagnosisResults />,
    responsive: ['desktop'],
  },
  // 支援サービス一覧
  {
    path: TENANT_URL.SUPPORT_SERVICES,
    title: '支援サービス管理',
    element: <SupportServicesList />,
    responsive: ['desktop'],
  },
  // 支援サービス作成
  {
    path: TENANT_URL.SUPPORT_SERVICES_CREATE,
    title: '支援サービス管理',
    element: <SupportServicesFormPage />,
    responsive: ['desktop'],
  },
  // 支援サービス編集
  {
    path: TENANT_URL.SUPPORT_SERVICES_EDIT,
    title: '支援サービス管理',
    element: <SupportServicesFormPage />,
    responsive: ['desktop'],
  },
  // 支援サービスプレビュー
  {
    path: TENANT_URL.SUPPORT_SERVICES_PREVIEW,
    title: '支援サービス管理',
    element: <SupportServicePreviewForm />,
    responsive: ['desktop'],
  },
  // 支援サービスカテゴリ一覧
  {
    path: TENANT_URL.SUPPORT_SERVICES_CATEGORIES,
    title: '支援サービス-カテゴリ管理',
    element: <SupportServiceCategoryList />,
    responsive: ['desktop'],
  },
  // 支援サービスタグ一覧
  {
    path: TENANT_URL.SUPPORT_SERVICES_TAGS,
    title: '支援サービス-タグ管理',
    element: <SupportServiceTagList />,
    responsive: ['desktop'],
  },
  // 企業一覧
  {
    path: TENANT_URL.COMPANIES,
    title: '企業管理',
    element: <CompanyList />,
    responsive: ['desktop'],
  },
  // イベント一覧
  {
    path: TENANT_URL.EVENTS,
    title: 'イベント管理',
    element: <EventList />,
    responsive: ['desktop'],
  },
  // イベント新規作成
  {
    path: TENANT_URL.EVENTS_CREATE,
    title: 'イベント管理',
    element: <EventFormPage />,
    responsive: ['desktop'],
  },
  // イベント編集
  {
    path: TENANT_URL.EVENTS_EDIT,
    title: 'イベント管理',
    element: <EventFormPage />,
    responsive: ['desktop'],
  },
  // イベントプレビュー
  {
    path: TENANT_URL.EVENTS_PREVIEW,
    title: 'イベント管理',
    element: <EventPreviewForm />,
    responsive: ['desktop'],
  },
  // 企業編集
  {
    path: TENANT_URL.COMPANIES_EDIT,
    title: '企業管理',
    element: <CompanyEdit />,
    responsive: ['desktop'],
  },
  // 企業ユーザー編集
  {
    path: TENANT_URL.COMPANY_USERS_EDIT,
    title: '企業ユーザー',
    element: <CompanyUserEdit />,
    responsive: ['desktop'],
  },
  // プロフィール
  {
    path: TENANT_URL.PROFILE,
    title: 'マイプロフィール',
    element: <Profile />,
    responsive: ['desktop', 'mobile'],
  },
  // テナント編集
  {
    path: TENANT_URL.TENANTS_EDIT,
    title: 'テナント管理',
    element: <TenantsEdit />,
    responsive: ['desktop'],
  },
  // テナントユーザー一覧
  {
    path: TENANT_URL.TENANT_USERS,
    title: 'テナントユーザー管理',
    element: <TenantUserList />,
    responsive: ['desktop'],
  },
  // テナントユーザー編集
  {
    path: TENANT_URL.TENANT_USERS_EDIT,
    title: 'テナントユーザー管理',
    element: <TenantUserEdit />,
    responsive: ['desktop'],
  },
  // テナントユーザー登録
  {
    path: TENANT_URL.TENANT_USERS_CREATE,
    title: 'テナントユーザー管理',
    element: <TenantUserCreate />,
    responsive: ['desktop'],
  },
  // 企業ユーザー一覧
  {
    path: TENANT_URL.COMPANY_USERS,
    title: '企業ユーザー',
    element: <CompanyUserList />,
    responsive: ['desktop'],
  },
  // ポータル管理
  {
    path: TENANT_URL.COMMUNITY_DASHBOARD,
    title: 'ダッシュボードTOP',
    element: <CommunityIndex />,
    responsive: ['desktop'],
  },
  // 参加者別アクティビティ一覧
  {
    path: TENANT_URL.COMMUNITY_LIST_PARTICIPANT,
    title: '参加者別アクティビティ',
    element: <ListParticipant />,
    responsive: ['desktop', 'mobile'],
  },
  // チャンネル別アクティビティ一覧
  {
    path: TENANT_URL.COMMUNITY_CHANNEL_ACTIVITY,
    title: 'チャンネル別アクティビティ',
    element: <CommunityChannelActivityList />,
    responsive: ['desktop'],
  },
  // ログアウト
  {
    path: TENANT_URL.LOGOUT,
    title: 'ログアウト',
    element: <TenantLogout />,
    responsive: false,
  },
];

export default tenantRoutes;
